import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo, useEffect } from "react";
import {
  getBuildDiagnosticsPrefs,
  saveBuildDiagnosticsPrefs,
} from "@/lib/build-diagnostics-prefs.functions";
import {
  getBuildDiagnostics,
  getFailureLog,
} from "@/lib/build-diagnostics.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/_authenticated/admin/build-diagnostics")({
  component: BuildDiagnosticsPage,
});

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function highlightMatch(text: string, query: string) {
  if (!query.trim()) return text;
  const q = query.trim().toLowerCase();
  const idx = text.toLowerCase().indexOf(q);
  if (idx === -1) return text;
  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + q.length);
  const after = text.slice(idx + q.length);
  return (
    <>
      {before}
      <span className="bg-amber-500/30 text-amber-100">{match}</span>
      {after}
    </>
  );
}

function BuildDiagnosticsPage() {
  const fetchDiag = useServerFn(getBuildDiagnostics);
  const fetchLog = useServerFn(getFailureLog);
  const loadPrefs = useServerFn(getBuildDiagnosticsPrefs);
  const savePrefs = useServerFn(saveBuildDiagnosticsPrefs);
  const [selected, setSelected] = useState<string | null>(null);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "failed" | "succeeded">("all");
  const [nameQuery, setNameQuery] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [highlightEnabled, setHighlightEnabled] = useState(true);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await loadPrefs();
        if (cancelled) return;
        setDateFrom(p.dateFrom);
        setDateTo(p.dateTo);
        setStatusFilter(p.statusFilter);
        setNameQuery(p.nameQuery);
        setPageSize(p.pageSize);
        setHighlightEnabled(p.highlightEnabled);
      } catch {
        // keep defaults on failure
      } finally {
        if (!cancelled) setPrefsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadPrefs]);

  useEffect(() => {
    if (!prefsLoaded) return;
    const handle = setTimeout(() => {
      void savePrefs({
        data: { dateFrom, dateTo, statusFilter, nameQuery, pageSize, highlightEnabled },
      }).catch(() => {
        // ignore transient save errors
      });
    }, 400);
    return () => clearTimeout(handle);
  }, [prefsLoaded, dateFrom, dateTo, statusFilter, nameQuery, pageSize, highlightEnabled, savePrefs]);

  const diag = useQuery({
    queryKey: ["admin", "build-diagnostics"],
    queryFn: () => fetchDiag(),
    refetchOnWindowFocus: false,
  });

  const activeName = selected ?? diag.data?.latest?.name ?? null;

  const log = useQuery({
    queryKey: ["admin", "build-diagnostics", "log", activeName],
    queryFn: () => fetchLog({ data: { name: activeName! } }),
    enabled: !!activeName,
    refetchOnWindowFocus: false,
  });

  const filteredFiles = useMemo(() => {
    if (!diag.data?.files) return [];
    let list = diag.data.files;

    if (dateFrom) {
      const from = new Date(dateFrom).getTime();
      list = list.filter((f) => new Date(f.modifiedAt).getTime() >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo).getTime();
      list = list.filter((f) => new Date(f.modifiedAt).getTime() <= to);
    }
    if (statusFilter === "failed") {
      list = list.filter((f) => f.name.includes("fail"));
    }
    if (statusFilter === "succeeded") {
      list = list.filter((f) => !f.name.includes("fail"));
    }
    if (nameQuery.trim()) {
      const q = nameQuery.trim().toLowerCase();
      list = list.filter((f) => f.name.toLowerCase().includes(q));
    }
    return list;
  }, [diag.data?.files, dateFrom, dateTo, statusFilter, nameQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredFiles.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedFiles = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredFiles.slice(start, start + pageSize);
  }, [filteredFiles, safePage, pageSize]);

  const hasFailures = (diag.data?.files.length ?? 0) > 0;
  const status = !diag.data
    ? "loading"
    : !diag.data.available
    ? "unknown"
    : hasFailures
    ? "failed"
    : "ok";

  const statusBadge = {
    loading: { label: "Loading…", cls: "border-white/20 bg-white/5 text-muted-foreground" },
    unknown: { label: "Unknown", cls: "border-amber-500/40 bg-amber-500/10 text-amber-200" },
    failed: { label: `Last build failed (${diag.data?.files.length} log${(diag.data?.files.length ?? 0) === 1 ? "" : "s"})`, cls: "border-red-500/40 bg-red-500/10 text-red-200" },
    ok: { label: "No failure logs recorded", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" },
  }[status];

  return (
    <div className="space-y-4">
      <header className="flex flex-col items-start justify-between gap-3 sm:flex-row">
        <div>
          <h2 className="text-lg font-semibold">Build diagnostics</h2>
          <p className="text-xs text-muted-foreground">
            Surfaces failure logs written by <code className="font-mono">scripts/build-with-log.mjs</code> into the project <code className="font-mono">logs/</code> directory.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded border px-2 py-1 text-xs ${statusBadge.cls}`}>{statusBadge.label}</span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const header = "name,bytes,modifiedAt,status";
              const rows = filteredFiles.map((f) => {
                const status = f.name.includes("fail") ? "failed" : "succeeded";
                const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
                return [esc(f.name), f.bytes, esc(f.modifiedAt), status].join(",");
              });
              const csv = [header, ...rows].join("\n");
              const blob = new Blob([csv], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `build-diagnostics-${new Date().toISOString().replace(/[:.]/g, "-")}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }}
            disabled={filteredFiles.length === 0}
          >
            Export CSV
          </Button>
          <Button size="sm" onClick={() => diag.refetch()} disabled={diag.isFetching}>
            {diag.isFetching ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </header>

      {diag.error && (
        <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">
          {(diag.error as Error).message}
        </div>
      )}

      {diag.data?.note && (
        <div className="rounded border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
          {diag.data.note}
        </div>
      )}

      {diag.data && (
        <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
          <aside className="rounded border border-white/10 bg-card/40 p-2">
            <div className="space-y-2 px-2 py-1">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Filters
              </div>
              <Input
                placeholder="Search by name…"
                value={nameQuery}
                onChange={(e) => {
                  setNameQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="h-8 text-xs"
              />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-1 block text-[10px] text-muted-foreground">From</label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => {
                      setDateFrom(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] text-muted-foreground">To</label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => {
                      setDateTo(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-[10px] text-muted-foreground">Status</label>
                <Select value={statusFilter} onValueChange={(v) => {
                  setStatusFilter(v as "all" | "failed" | "succeeded");
                  setCurrentPage(1);
                }}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="succeeded">Succeeded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1 block text-[10px] text-muted-foreground">Per page</label>
                <Select value={String(pageSize)} onValueChange={(v) => {
                  setPageSize(Number(v));
                  setCurrentPage(1);
                }}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded border border-white/10 px-2 py-1.5">
                <Label htmlFor="highlight-toggle" className="text-[10px] text-muted-foreground cursor-pointer">
                  Highlight matches
                </Label>
                <Switch
                  id="highlight-toggle"
                  checked={highlightEnabled}
                  onCheckedChange={setHighlightEnabled}
                  className="scale-75"
                />
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between px-2 py-1">
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Failure logs ({filteredFiles.length})
              </span>
              {filteredFiles.length > pageSize && (
                <span className="text-[10px] text-muted-foreground">
                  Page {safePage} / {totalPages}
                </span>
              )}
            </div>
            {filteredFiles.length === 0 ? (
              <div className="px-2 py-3 text-xs text-muted-foreground">
                {statusFilter === "succeeded"
                  ? "Only failed builds generate logs. No succeeded build logs are recorded."
                  : "No logs match the current filters."}
              </div>
            ) : (
              <>
                <ul className="space-y-1">
                  {paginatedFiles.map((f) => {
                    const active = (activeName ?? "") === f.name;
                    return (
                      <li key={f.name}>
                        <button
                          onClick={() => setSelected(f.name)}
                          className={`w-full rounded px-2 py-1.5 text-left text-xs transition-colors ${
                            active
                              ? "bg-primary/10 text-foreground"
                              : "text-muted-foreground hover:bg-white/5 hover:text-foreground"
                          }`}
                        >
                          <div className="truncate font-mono">{highlightEnabled ? highlightMatch(f.name, nameQuery) : f.name}</div>
                          <div className="mt-0.5 flex justify-between text-[10px] opacity-70">
                            <span>{new Date(f.modifiedAt).toLocaleString()}</span>
                            <span>{formatBytes(f.bytes)}</span>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {totalPages > 1 && (
                  <div className="mt-2 flex items-center justify-between px-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[10px]"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={safePage <= 1}
                    >
                      Prev
                    </Button>
                    <span className="text-[10px] text-muted-foreground">
                      {safePage} / {totalPages}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[10px]"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={safePage >= totalPages}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </aside>

          <section className="rounded border border-white/10 bg-background/60 p-3">
            {!activeName ? (
              <div className="text-xs text-muted-foreground">Select a failure log to inspect its tail.</div>
            ) : log.isLoading ? (
              <div className="text-xs text-muted-foreground">Loading {activeName}…</div>
            ) : log.error ? (
              <div className="text-xs text-red-300">{(log.error as Error).message}</div>
            ) : log.data ? (
              <>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-mono text-xs text-foreground">{log.data.name}</h3>
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <span>{formatBytes(log.data.bytes)}{log.data.truncated ? " (tail)" : ""}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const blob = new Blob([log.data!.content], { type: "text/plain" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = log.data!.name;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }}
                    >
                      Download
                    </Button>
                  </div>
                </div>
                <pre className="max-h-[600px] overflow-auto whitespace-pre-wrap break-all text-[11px] leading-relaxed text-muted-foreground">
                  {log.data.content || "(empty)"}
                </pre>
              </>
            ) : null}
          </section>
        </div>
      )}
    </div>
  );
}